import React, { useEffect, useState } from "react";
import {
  KeyRound, X, Loader2, Eye, EyeOff, CheckCircle2, AlertTriangle, ShieldCheck,
  Clock,
} from "lucide-react";
import { Badge } from "./Primitives";
import { rotateCredentials } from "../lib/s3Api";
import { useRole } from "../lib/role.jsx";
import { cn } from "../lib/utils";

// -----------------------------------------------------------------------------
// Credential Rotation Modal · IT Head only.
// Accepts fresh AWS STS temporary credentials (access key / secret / session
// token) and a session-duration estimate so the pilot preflight tile can show
// a countdown badge. Submits to POST /api/s3/connections/{id}/rotate-credentials
// which writes to backend/.env AND os.environ, runs an immediate health probe,
// then returns the new expiry UTC ISO.
// -----------------------------------------------------------------------------
export default function CredentialRotationModal({ open, onClose, connection, onDone }) {
  const { user } = useRole();
  const [akid, setAkid] = useState("");
  const [secret, setSecret] = useState("");
  const [token, setToken] = useState("");
  const [showSecret, setShowSecret] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [duration, setDuration] = useState(60);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!open) {
      // reset on close so the next open starts clean
      setAkid(""); setSecret(""); setToken(""); setDuration(60);
      setError(null); setResult(null); setBusy(false);
      setShowSecret(false); setShowToken(false);
    }
  }, [open]);

  if (!open) return null;

  const isItHead = user?.role === "it_head";
  const canSubmit = isItHead && akid.trim() && secret.trim() && token.trim() && !busy;

  async function onSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError(null); setResult(null);
    try {
      const r = await rotateCredentials(connection.id, {
        aws_access_key_id: akid.trim(),
        aws_secret_access_key: secret.trim(),
        aws_session_token: token.trim(),
        session_duration_min: Number(duration) || 60,
        actor: user?.employeeId,
        role: user?.role,
      });
      setResult(r);
      // Give the operator ~1.4s to see the success, then propagate up so
      // the Live Data Pipeline refreshes the connections list.
      setTimeout(() => { onDone?.(r); onClose?.(); }, 1400);
    } catch (e) {
      setError(e.message || "Rotation failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
         onClick={() => !busy && onClose?.()}
         data-testid="cred-rotate-modal">
      <div className="w-full max-w-xl bg-surface border border-border-strong rounded-sm shadow-2xl max-h-[92vh] overflow-y-auto"
           onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-border">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="w-8 h-8 rounded-sm bg-warning/15 text-warning flex items-center justify-center shrink-0">
              <KeyRound size={14} />
            </div>
            <div className="min-w-0">
              <div className="text-[13px] font-semibold text-fg leading-none">Rotate pilot AWS credentials</div>
              <div className="text-[10.5px] text-muted mt-1">
                IT Head · writes to pod .env + hot-swaps in-memory (survives restart)
              </div>
            </div>
          </div>
          <button data-testid="cred-rotate-close" onClick={() => !busy && onClose?.()}
                  disabled={busy}
                  className="w-7 h-7 flex items-center justify-center hover:bg-surface-2 rounded-sm disabled:opacity-40">
            <X size={14} />
          </button>
        </div>

        {/* Body */}
        {!isItHead ? (
          <div className="p-6 text-center text-[12px] text-muted" data-testid="cred-rotate-restricted">
            <ShieldCheck size={28} className="mx-auto mb-2 text-warning opacity-70" />
            <div className="text-fg text-sm font-medium mb-1">Restricted action</div>
            This control is reserved for the IT Head role.
          </div>
        ) : (
          <form onSubmit={onSubmit} className="p-5 space-y-3.5">
            <div className="text-[10.5px] bg-surface-2/60 border border-border rounded-sm p-2.5 leading-relaxed">
              Paste the three AWS STS session values from 1Rotary's handoff. We
              write them to the pod environment (<code className="font-mono-tabular text-primary">backend/.env</code>){" "}
              AND hot-swap <code className="font-mono-tabular text-primary">os.environ</code> so the next S3 call
              uses the new session without a pod restart. We never log the
              values, and the connection's <strong>consecutive failures</strong> counter resets to zero.
            </div>

            <Field label="AWS_ACCESS_KEY_ID" testid="cred-field-akid">
              <input
                type="text"
                data-testid="cred-akid"
                value={akid}
                onChange={e => setAkid(e.target.value)}
                autoComplete="off"
                spellCheck="false"
                placeholder="ASIA…"
                className="w-full bg-bg border border-border px-3 py-2 rounded-sm text-[12px] font-mono-tabular focus:outline-none focus:border-primary"
                disabled={busy}
              />
            </Field>

            <Field label="AWS_SECRET_ACCESS_KEY" testid="cred-field-secret">
              <div className="relative">
                <input
                  type={showSecret ? "text" : "password"}
                  data-testid="cred-secret"
                  value={secret}
                  onChange={e => setSecret(e.target.value)}
                  autoComplete="new-password"
                  spellCheck="false"
                  placeholder="40-char secret"
                  className="w-full bg-bg border border-border pl-3 pr-9 py-2 rounded-sm text-[12px] font-mono-tabular focus:outline-none focus:border-primary"
                  disabled={busy}
                />
                <button type="button" onClick={() => setShowSecret(v => !v)}
                        data-testid="cred-secret-toggle"
                        className="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-muted hover:text-fg">
                  {showSecret ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </Field>

            <Field label="AWS_SESSION_TOKEN" testid="cred-field-token">
              <div className="relative">
                <textarea
                  data-testid="cred-token"
                  value={token}
                  onChange={e => setToken(e.target.value)}
                  autoComplete="off"
                  spellCheck="false"
                  rows={showToken ? 5 : 3}
                  placeholder="Opaque base64 blob · click eye to expand"
                  className={cn(
                    "w-full bg-bg border border-border px-3 py-2 pr-9 rounded-sm text-[11px] font-mono-tabular focus:outline-none focus:border-primary resize-none break-all",
                    !showToken && "blur-[2px] focus:blur-0",
                  )}
                  disabled={busy}
                />
                <button type="button" onClick={() => setShowToken(v => !v)}
                        data-testid="cred-token-toggle"
                        className="absolute right-2 top-2 w-6 h-6 flex items-center justify-center text-muted hover:text-fg">
                  {showToken ? <EyeOff size={13} /> : <Eye size={13} />}
                </button>
              </div>
            </Field>

            <Field label="Session duration (minutes)" testid="cred-field-duration">
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  data-testid="cred-duration"
                  value={duration}
                  min={15}
                  max={720}
                  onChange={e => setDuration(e.target.value)}
                  className="w-28 bg-bg border border-border px-3 py-2 rounded-sm text-[12px] font-mono-tabular focus:outline-none focus:border-primary"
                  disabled={busy}
                />
                <span className="text-[11px] text-muted inline-flex items-center gap-1">
                  <Clock size={10} /> Drives the countdown badge
                </span>
                <div className="ml-auto flex items-center gap-1">
                  {[60, 180, 360].map(d => (
                    <button key={d} type="button"
                            data-testid={`cred-duration-${d}`}
                            onClick={() => setDuration(d)}
                            disabled={busy}
                            className={cn(
                              "px-2 py-1 text-[10.5px] border rounded-sm",
                              Number(duration) === d
                                ? "border-primary bg-primary/10 text-primary"
                                : "border-border text-muted hover:border-primary hover:text-primary",
                            )}>
                      {d < 60 ? `${d}m` : `${d / 60}h`}
                    </button>
                  ))}
                </div>
              </div>
            </Field>

            {error && (
              <div className="text-[11.5px] text-critical bg-critical/5 border border-critical/30 rounded-sm p-2.5 leading-snug"
                   data-testid="cred-rotate-error">
                <AlertTriangle size={11} className="inline mr-1" />
                {error}
              </div>
            )}

            {result && (
              <div className="text-[11.5px] text-success bg-success/5 border border-success/30 rounded-sm p-2.5 leading-snug"
                   data-testid="cred-rotate-success">
                <CheckCircle2 size={11} className="inline mr-1" />
                Rotation successful.{" "}
                {result.probe?.ok
                  ? <>AWS probe OK · <strong>{result.probe.sample_key_count ?? 0}</strong> keys visible.</>
                  : <>AWS probe reported: <code className="font-mono-tabular">{result.probe?.error || "see audit"}</code></>}
              </div>
            )}

            <div className="flex items-center gap-2 pt-2">
              <Badge status="info">
                <KeyRound size={10} className="inline mr-1" />
                target: {connection?.bucket}
              </Badge>
              <div className="ml-auto flex items-center gap-2">
                <button type="button" onClick={() => onClose?.()} disabled={busy}
                        data-testid="cred-rotate-cancel"
                        className="px-3 py-1.5 text-[11.5px] border border-border rounded-sm hover:bg-surface-2 disabled:opacity-40">
                  Cancel
                </button>
                <button type="submit" disabled={!canSubmit}
                        data-testid="cred-rotate-submit"
                        className="px-4 py-1.5 text-[11.5px] font-semibold bg-primary text-primary-fg hover:opacity-90 rounded-sm inline-flex items-center gap-1 disabled:opacity-40">
                  {busy ? <Loader2 size={11} className="animate-spin" /> : <KeyRound size={11} />}
                  {busy ? "Rotating…" : "Rotate & verify"}
                </button>
              </div>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-muted font-semibold mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
